import React, { useState } from 'react'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MessageSquareIcon,
  MailIcon,
  SmartphoneIcon,
} from 'lucide-react'

export interface OtherConversationSummary {
  id: string
  channel: 'whatsapp' | 'sms' | 'email'
  subject?: string | null
  contact_name: string
  contact_info: string
  last_message: string | null
  last_message_at: string | null
  unread_count: number
}

interface CustomerOtherConversationsProps {
  customerName: string
  conversations: OtherConversationSummary[]
  onSelect: (id: string) => void
  className?: string
}

const CHANNEL_ICON: Record<OtherConversationSummary['channel'], { icon: React.ElementType; color: string }> = {
  whatsapp: { icon: MessageSquareIcon, color: 'text-green-600' },
  sms: { icon: SmartphoneIcon, color: 'text-blue-600' },
  email: { icon: MailIcon, color: 'text-orange-600' },
}

function shortTime(dateStr: string | null) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Compact quick-switch panel shown under the conversation header: the other
 * (separate) conversations linked to the same customer. Selecting one switches
 * the open thread — it never merges them.
 */
export function CustomerOtherConversations({
  customerName,
  conversations,
  onSelect,
  className = '',
}: CustomerOtherConversationsProps) {
  const [expanded, setExpanded] = useState(false)
  if (conversations.length === 0) return null

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)

  return (
    <div className={`flex-shrink-0 border-b border-gray-200 bg-white ${className}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-1.5 px-5 py-2 text-xs text-gray-600 hover:bg-gray-50"
      >
        {expanded ? (
          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
        ) : (
          <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
        )}
        <span className="font-medium">
          {conversations.length} other conversation{conversations.length === 1 ? '' : 's'} with {customerName}
        </span>
        {totalUnread > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {expanded && (
        <div className="max-h-44 overflow-y-auto divide-y divide-gray-50 border-t border-gray-100">
          {conversations.map((c) => {
            const meta = CHANNEL_ICON[c.channel] || CHANNEL_ICON.email
            const Icon = meta.icon
            const title = (c.channel === 'email' && c.subject) || c.contact_name || c.contact_info
            return (
              <button
                key={c.id}
                onClick={() => onSelect(c.id)}
                className="w-full flex items-center gap-2 px-5 py-1.5 text-left hover:bg-gray-50"
              >
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${meta.color}`} />
                <span className="flex-1 min-w-0 text-xs text-gray-800 truncate">{title}</span>
                <span className="text-[10px] text-gray-400 flex-shrink-0">{shortTime(c.last_message_at)}</span>
                {c.unread_count > 0 && <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CustomerOtherConversations
